import { currentUserQuery } from "@common/api"
import { useLoginMutation, useLogoutMutation } from "@common/api/auth"
import type { BodyLoginLoginAccessToken as AccessToken } from "@common/client"
import { getErrorMessage } from "@common/lib/error-utils"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useRouter } from "@tanstack/react-router"
import { AxiosError } from "axios"
import { useCallback, useEffect, useState } from "react"

// Guard against open redirects: only same-origin relative paths (not "//host").
function isSafeRedirect(target: string): boolean {
  return target.startsWith("/") && !target.startsWith("//")
}

const isLoggedIn = () => {
  return localStorage.getItem("access_token") !== null
}

const useAuth = () => {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    ...currentUserQuery(),
    enabled: isLoggedIn(),
    retry: (count, error: unknown) => {
      console.log("Auth failed", error)
      const isAxios = error instanceof AxiosError
      const isReal = isAxios && (error.status === 401 || error.status === 403)
      return isReal ? false : count < 3
    },
  })

  useEffect(() => {
    if (isError && isLoggedIn()) {
      localStorage.removeItem("access_token")
      navigate({ to: "/login" })
    }
  }, [isError, navigate])

  const loginMutation = useLoginMutation()
  const logoutMutation = useLogoutMutation()

  const login = useCallback(
    (data: AccessToken, redirectTo?: string) => {
      return loginMutation.mutateAsync(data, {
        onSuccess: (response) => {
          if (response.must_change_password) {
            navigate({ to: "/change-password" })
          } else if (redirectTo && isSafeRedirect(redirectTo)) {
            router.history.push(redirectTo)
          } else {
            navigate({ to: "/" })
          }
        },
        onError: (e) => {
          setError(getErrorMessage(e, "Unable to log in. Please try again."))
        },
      })
    },
    [loginMutation, navigate, router],
  )

  const logout = useCallback(() => {
    return logoutMutation.mutateAsync(
      {},
      {
        onSettled: () => {
          queryClient.clear()
          localStorage.removeItem("access_token")
          navigate({ to: "/login" })
        },
      },
    )
  }, [logoutMutation, queryClient, navigate])

  return {
    login,
    logout,
    user,
    isLoading,
    error,
    resetError: () => setError(null),
  }
}

export { isLoggedIn }
export default useAuth
